import { colors, fonts, fmt, fmtPct } from './UI'

export function calcHealthScore({ savingsRate, savingsRateTarget, emergencyFundMonths, emergencyFundTargetMonths, debtToIncome, goalProgress, hasGoals, budgetAdherence }) {
  let score = 0
  const target = savingsRateTarget || 20
  const efTarget = emergencyFundTargetMonths || 6

  // Savings rate (0-30 points)
  if (savingsRate >= target) score += 30
  else if (savingsRate >= target * 0.75) score += 22
  else if (savingsRate >= target * 0.5) score += 14
  else if (savingsRate >= target * 0.25) score += 7
  else if (savingsRate > 0) score += 3

  // Emergency fund (0-25 points) - uses user's own target
  if (emergencyFundMonths >= efTarget) score += 25
  else if (emergencyFundMonths >= efTarget * 0.5) score += 18
  else if (emergencyFundMonths >= 1) score += 10
  else if (emergencyFundMonths > 0) score += 4

  // Debt to income (0-25 points)
  if (debtToIncome === 0) score += 25
  else if (debtToIncome < 0.1) score += 20
  else if (debtToIncome < 0.2) score += 14
  else if (debtToIncome < 0.35) score += 8
  else if (debtToIncome < 0.5) score += 3

  // Goal progress (0-20 points)
  if (!hasGoals) score += 10
  else if (goalProgress >= 50) score += 20
  else if (goalProgress >= 25) score += 14
  else if (goalProgress >= 10) score += 8
  else score += 3

  score = Math.min(100, Math.round(score))

  // Debt ceiling: heavy debt should dominate the picture, so a high
  // debt-to-income ratio caps the overall score regardless of how strong the
  // other pillars look. Prevents "GOOD" while deeply in debt. The cap sits at
  // the top of each band so the score still moves with the other pillars below
  // the ceiling, but can't escape into a misleadingly healthy label.
  if (debtToIncome >= 0.6) score = Math.min(score, 39)       // "Needs work"
  else if (debtToIncome >= 0.45) score = Math.min(score, 44) // top of "Needs work"
  else if (debtToIncome >= 0.3) score = Math.min(score, 60)  // capped in "Fair"

  return score
}

function scoreLabel(score) {
  if (score >= 80) return { label: 'Excellent', color: '#3db87a' }
  if (score >= 65) return { label: 'Good', color: colors.gold }
  if (score >= 45) return { label: 'Fair', color: '#e8a84b' }
  return { label: 'Needs work', color: colors.red }
}

function pillarStatus(data) {
  const { savingsRate, savingsRateTarget, emergencyFundMonths, emergencyFundTargetMonths, emergencyBalance, totalDebt, totalIncome, goalProgress, hasGoals, budgetOverCount } = data
  const target = savingsRateTarget || 20
  const efTarget = emergencyFundTargetMonths || 6

  // Emergency fund can only be expressed in "months covered" if we know monthly
  // expenses. If there's a tagged balance but no expense baseline (no budget and
  // no spend), say so honestly rather than implying no fund exists.
  const hasEmergencyBalance = (emergencyBalance || 0) > 0
  const canMeasureMonths = emergencyFundMonths > 0

  return [
    {
      label: 'Emergency fund',
      icon: 'ti-shield',
      value: canMeasureMonths ? `${emergencyFundMonths.toFixed(1)} months covered` : hasEmergencyBalance ? `${fmt(emergencyBalance)} set aside` : 'No emergency fund tagged',
      status: canMeasureMonths
        ? (emergencyFundMonths >= efTarget ? `Fully funded - ${efTarget}+ months of expenses covered` : emergencyFundMonths >= efTarget * 0.5 ? `${emergencyFundMonths.toFixed(1)} months covered - you need at least ${efTarget}` : `Only ${emergencyFundMonths.toFixed(1)} months covered - this is your top priority`)
        : hasEmergencyBalance ? `${fmt(emergencyBalance)} tagged - set a budget or log spending to see how many months that covers` : 'Tag a savings account as your emergency fund to track this',
      ok: emergencyFundMonths >= efTarget,
    },
    {
      label: 'Monthly savings rate',
      icon: 'ti-coin',
      value: savingsRate > 0 ? `${Math.round(savingsRate)}% of income saved` : 'No savings recorded yet',
      status: savingsRate >= target ? `On target - ${Math.round(savingsRate)}% vs your ${target}% goal` : `${Math.round(savingsRate)}% - your target is ${target}%. ${totalIncome > 0 ? `Adding ${fmt(totalIncome * (target - savingsRate) / 100)}/mo would close the gap.` : 'Set your income in Settings.'}`,
      ok: savingsRate >= target,
    },
    {
      label: 'Savings goals progress',
      icon: 'ti-target',
      value: !hasGoals ? 'No goals set' : `${Math.round(goalProgress)}% progress across all goals`,
      status: !hasGoals ? 'Set savings goals to track your progress toward specific targets' : goalProgress >= 50 ? `${Math.round(goalProgress)}% of the way to your goals - great progress` : `${Math.round(goalProgress)}% progress - increase monthly contributions to hit your targets sooner`,
      ok: hasGoals && goalProgress >= 25,
    },
    {
      label: 'Debt',
      icon: 'ti-credit-card',
      value: totalDebt > 0 ? `${fmt(totalDebt)} outstanding` : 'Debt free',
      status: totalDebt === 0 ? 'Debt free - excellent position' : totalIncome > 0 && (totalDebt / (totalIncome * 12)) < 0.2 ? `Debt-to-income ratio is healthy at ${Math.round(totalDebt / (totalIncome * 12) * 100)}%` : totalIncome > 0 ? `Debt-to-income ratio is ${Math.round(totalDebt / (totalIncome * 12) * 100)}% - aim to get below 20%` : `${fmt(totalDebt)} outstanding`,
      ok: totalDebt === 0 || (totalIncome > 0 && totalDebt / (totalIncome * 12) < 0.2),
    },
    {
      label: 'Budget',
      icon: 'ti-wallet',
      value: budgetOverCount > 0 ? `${budgetOverCount} categor${budgetOverCount === 1 ? 'y' : 'ies'} over budget` : 'All categories within budget',
      status: budgetOverCount === 0 ? 'Within budget across all categories this month' : `${budgetOverCount} categor${budgetOverCount === 1 ? 'y' : 'ies'} over budget this month - review your spending`,
      ok: budgetOverCount === 0,
    },
  ]
}

export default function HealthScore({ score, data, compact }) {
  const { label, color } = scoreLabel(score)
  const circumference = 2 * Math.PI * 42
  const dashOffset = circumference - (score / 100) * circumference
  const pillars = pillarStatus(data)

  // Investment ready signal
  const investmentReady = data.emergencyFundMonths >= (data.emergencyFundTargetMonths || 3) && data.totalDebt === 0 && data.savingsRate >= 15

  if (compact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <svg width="56" height="56" viewBox="0 0 100 100" aria-label={`Financial health score: ${score}`}>
          <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="10" />
          <circle cx="50" cy="50" r="42" fill="none" stroke={color} strokeWidth="10"
            strokeDasharray={circumference} strokeDashoffset={dashOffset}
            strokeLinecap="round" transform="rotate(-90 50 50)" style={{ transition: 'stroke-dashoffset 1s ease' }} />
          <text x="50" y="54" textAnchor="middle" fill={color} fontSize="22" fontFamily="Manrope, sans-serif" fontWeight="700">{score}</text>
        </svg>
        <div>
          <div style={{ fontSize: '14px', color, fontFamily: fonts.heading, fontWeight: 700, letterSpacing: '0.04em' }}>{label}</div>
          <div style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body }}>Financial health score</div>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Score ring */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
        <svg width="90" height="90" viewBox="0 0 100 100" aria-label={`Score: ${score}/100`}>
          <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="8" />
          <circle cx="50" cy="50" r="42" fill="none" stroke={color} strokeWidth="8"
            strokeDasharray={circumference} strokeDashoffset={dashOffset}
            strokeLinecap="round" transform="rotate(-90 50 50)" style={{ transition: 'stroke-dashoffset 1s ease' }} />
          <text x="50" y="46" textAnchor="middle" fill={color} fontSize="26" fontFamily="Manrope, sans-serif" fontWeight="700">{score}</text>
          <text x="50" y="60" textAnchor="middle" fill={colors.muted} fontSize="9" fontFamily="Manrope, sans-serif">/100</text>
        </svg>
        <div>
          <div style={{ fontSize: '32px', color, fontFamily: fonts.heading, fontWeight: 700, letterSpacing: '0.04em' }}>{label}</div>
          <div style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, marginTop: '4px' }}>Your financial health score</div>
        </div>
      </div>

      {/* Pillar breakdown */}
      {pillars.map(p => (
        <div key={p.label} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px 0', borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '2px', background: p.ok ? 'rgba(61,184,122,0.1)' : 'rgba(224,82,82,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className={`ti ${p.icon}`} style={{ fontSize: '14px', color: p.ok ? colors.green : colors.red }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '14px', color: colors.text, fontFamily: fonts.body, fontWeight: 600, marginBottom: '3px' }}>{p.label}</div>
            <div style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, lineHeight: 1.5 }}>{p.status}</div>
          </div>
          <span style={{ fontSize: '16px', flexShrink: 0, marginTop: '4px', color: p.ok ? colors.green : colors.red }}>{p.ok ? '✓' : '!'}</span>
        </div>
      ))}

      {/* Investment ready signal */}
      {investmentReady && (
        <div style={{ marginTop: '16px', padding: '14px', background: 'rgba(201,151,43,0.08)', border: `1px solid ${colors.borderGold}`, borderRadius: '2px' }}>
          <div style={{ fontSize: '14px', color: colors.gold, fontFamily: fonts.body, fontWeight: 600, marginBottom: '4px' }}>
            🎯 You're investment ready
          </div>
          <div style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, lineHeight: 1.6, marginBottom: '10px' }}>
            Emergency fund in place, no debt, saving consistently. The Starting Line covers exactly what to do next.
          </div>
          <a href="https://theinvestingcouple.co.uk/the-starting-line" target="_blank" rel="noopener noreferrer" style={{ fontSize: '14px', color: colors.gold, fontFamily: fonts.body, fontWeight: 600, letterSpacing: '0.06em', textDecoration: 'none' }}>
            Find out more →
          </a>
        </div>
      )}
    </div>
  )
}
